import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createDataset,
  deleteDataset,
  fetchDatasets,
  fetchDatasetsPaginated,
  updateDataset
} from "../../../data/queries/datasets";
import {
  CreateDatasetRequest,
  DatasetFilters,
  PaginationParams,
  UpdateDatasetRequest
} from "../../../types/dataset";
import { useAuth } from "../../auth/AuthContext";

interface UseDatasetsOptions {
  pagination?: PaginationParams;
  filters?: DatasetFilters;
  enabled?: boolean;
}

// Query keys factory for better cache management
const datasetKeys = {
  all: ["datasets"] as const,
  lists: () => [...datasetKeys.all, "list"] as const,
  list: (filters?: DatasetFilters) => [...datasetKeys.lists(), { filters }] as const,
  paginated: (pagination: PaginationParams, filters?: DatasetFilters) =>
    [...datasetKeys.all, "paginated", { pagination, filters }] as const
};

export const useDatasets = (options: UseDatasetsOptions = {}) => {
  const { tenantId } = useAuth();
  const { pagination, filters, enabled = true } = options;
  const queryClient = useQueryClient();

  // Query for all datasets (non-paginated)
  const allDatasetsQuery = useQuery({
    queryKey: datasetKeys.list(filters),
    queryFn: () => fetchDatasets(filters),
    enabled: enabled && !pagination,
    select: (data) => data.success ? data.data : [],
    throwOnError: false
  });

  // Query for paginated datasets
  const paginatedDatasetsQuery = useQuery({
    queryKey: datasetKeys.paginated(pagination!, filters),
    queryFn: () => fetchDatasetsPaginated(pagination!, filters),
    enabled: enabled && !!pagination,
    select: (data) => data.success ? data.data : null,
    throwOnError: false
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (datasetData: CreateDatasetRequest) =>
      createDataset({ ...datasetData, tenant_id: tenantId || "" }),
    onSuccess: (result) => {
      if (result.success) {
        // Invalidate and refetch datasets
        queryClient.invalidateQueries({ queryKey: datasetKeys.all });
      }
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateDatasetRequest }) =>
      updateDataset(id, updates),
    onSuccess: (result) => {
      if (result.success) {
        // Just invalidate all dataset queries
        queryClient.invalidateQueries({ queryKey: datasetKeys.all });
      }
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDataset(id),
    onSuccess: (result) => {
      if (result.success) {
        // Just invalidate all dataset queries
        queryClient.invalidateQueries({ queryKey: datasetKeys.all });
      }
    }
  });

  // Determine which query to use based on pagination
  const activeQuery = pagination ? paginatedDatasetsQuery : allDatasetsQuery;

  // Extract datasets from the appropriate query
  const datasets = pagination
    ? (paginatedDatasetsQuery.data?.data || [])
    : (allDatasetsQuery.data || []);

  const paginatedData = pagination ? paginatedDatasetsQuery.data : null;

  // Handle errors from queries
  const queryError = activeQuery.error;
  const mutationError = createMutation.error || updateMutation.error || deleteMutation.error;
  const error = queryError || mutationError;

  return {
    // Data
    datasets,
    paginatedData,

    // Loading states
    loading: activeQuery.isLoading || activeQuery.isFetching,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Error handling
    error: error ? (error instanceof Error ? error.message : String(error)) : null,

    // Actions
    createDataset: createMutation.mutateAsync,
    updateDataset: (id: string, updates: UpdateDatasetRequest) =>
      updateMutation.mutateAsync({ id, updates }),
    deleteDataset: deleteMutation.mutateAsync,

    // Utilities
    refreshDatasets: () => queryClient.invalidateQueries({ queryKey: datasetKeys.all }),
    fetchDatasets: () => activeQuery.refetch()
  };
};
